import React from 'react';
import type { MenuProps } from 'antd';
import { Layout } from 'antd';
import { PlusOutlined, BarChartOutlined, BookOutlined } from '@ant-design/icons';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import { useAuthContext } from '../../context/auth';
import { useSettingsContext } from '../../context/settings';
import { AppHeader } from '../UI/app-header';
import { MenuSections } from '../../types/menu-sections';
import { CreativityType } from '../../types/banner';
import { DashboardPage, CreateBannerPage, CreativityFormPage, LibraryPage } from '../../pages';
import { SubtleButton } from './buttons';
import { isValidCreativityType } from '../../constants/clients/registry';
import { TreeView } from '../library/tree-view';
import { LibraryProvider } from '../../context/library';

const { Content } = Layout;
type MenuItem = Required<MenuProps>['items'][number];

export const AppLayout: React.FC = () => {
  const { isLoggedIn, loginUrl } = useAuthContext();

  if (!isLoggedIn) {
    return (
      <div className='login-page'>
        <div className='Button-container'>
          <SubtleButton onClick={() => (window.location.href = loginUrl)}>Ingresar</SubtleButton>
        </div>
      </div>
    );
  }

  // Si está logueado, renderizar el layout autenticado
  return <AuthenticatedLayout />;
};

export const AuthenticatedLayout: React.FC = () => {
  const { userInfo, signOut } = useAuthContext();
  const { settings } = useSettingsContext();

  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  // Mapear URL a MenuSections
  const getActiveKeyFromPath = (pathname: string): MenuSections => {
    if (pathname === '/') return 'dashboard';
    if (pathname === '/create-asset') return 'create-asset';

    if (pathname.startsWith('/library')) return 'library';
    if (pathname.startsWith('/creativity-form/')) {
      return pathname.slice(1) as MenuSections;
    }
    return 'dashboard';
  };

  const [activeKey, setActiveKey] = React.useState<MenuSections>(getActiveKeyFromPath(location.pathname));

  // Sincronizar activeKey con URL
  React.useEffect(() => {
    const newActiveKey = getActiveKeyFromPath(location.pathname);
    setActiveKey(newActiveKey);
  }, [location.pathname]);

  // Scrollear al top al cambiar de sección
  React.useEffect(() => {
    if (activeKey === 'library') return;

    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [activeKey]);

  // Función que combina setState + navigate
  const handleNavigate = (section: MenuSections) => {
    let path = '/';

    if (section === 'create-asset') path = '/create-asset';
    else if (section === 'library') path = '/library';
    else if (section.startsWith('creativity-form/')) path = `/${section}`;

    navigate(path);
  };

  // Componente wrapper para creativity con parámetros
  const CreativityFormWrapper = () => {
    const { creativityType } = useParams<{ creativityType: string }>();
    const decodedType = creativityType ? decodeURIComponent(creativityType) : '';

    if (!decodedType || !isValidCreativityType(decodedType)) {
      return <DashboardPage onNavigate={handleNavigate} />;
    }

    return <CreativityFormPage creativityType={decodedType as CreativityType} onNavigate={handleNavigate} />;
  };

  const getComponent = () => {
    if (activeKey === 'dashboard') {
      return <DashboardPage onNavigate={handleNavigate} />;
    }

    if (activeKey === 'create-asset') {
      return <CreateBannerPage onNavigate={handleNavigate} />;
    }

    if (activeKey === 'library') {
      return <LibraryPage />;
    }

    if (activeKey.startsWith('creativity-form/')) {
      return <CreativityFormWrapper />;
    }

    return <DashboardPage onNavigate={handleNavigate} />;
  };

  const items = React.useMemo(() => {
    const menuItems: MenuItem[] = [
      {
        key: 'dashboard',
        icon: <BarChartOutlined />,
        label: 'Dashboard'
      }
    ];

    // Solo agregar si la creación de banners no está deshabilitada
    if (!settings.disableBannerCreation) {
      menuItems.push({
        key: 'create-asset',
        icon: <PlusOutlined />,
        label: 'Nuevo asset'
      });
    }

    menuItems.push({
      key: 'library',
      icon: <BookOutlined />,
      label: 'Library'
    });

    return menuItems;
  }, [settings.disableBannerCreation]);

  const layoutContent = (
    <Layout style={{ display: 'flex', height: '100vh' }}>
      <AppHeader
        activeKey={activeKey}
        items={items}
        userInfo={userInfo}
        onKeySelect={handleNavigate}
        onSignOut={signOut}
        drawerExtraContent={activeKey === 'library' ? <TreeView darkMode={true} /> : undefined}
      />
      <Content ref={contentRef} style={{ overflow: 'auto', backgroundColor: '#F9FAFB' }}>
        {getComponent()}
      </Content>
    </Layout>
  );

  return activeKey === 'library' ? <LibraryProvider>{layoutContent}</LibraryProvider> : layoutContent;
};
